import React, { useCallback, useState } from "react";
import {
    Box, Button, Flex, Icon, Text, useColorModeValue, useDisclosure,
} from "@chakra-ui/react";
import { MdAdd, MdCurrencyExchange, MdRefresh } from "react-icons/md";
import QuickSeedPanel from "./QuickSeedPanel";
import RateFormDialog from "./RateFormDialog";
import RatesTable from "./RatesTable";
import useCurrencyRates from "./useCurrencyRates";

/**
 * Renders the page header with its brand badge, intro text and actions.
 * @param {Object} props - loading flag, refresh and create handlers
 * @returns {React.ReactElement}
 */
function PageHeader({ loading, onRefresh, onCreate }) {
    const badgeBg = useColorModeValue("brand.100", "whiteAlpha.100");
    const titleColor = useColorModeValue("secondaryGray.900", "white");
    const subColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");

    return (
        <Flex align="center" justify="space-between" gap="12px" mb="20px" wrap="wrap">
            <Flex align="center" gap="12px">
                <Flex
                    align="center" justify="center" w="44px" h="44px"
                    borderRadius="12px" bg={badgeBg} flexShrink="0"
                >
                    <Icon as={MdCurrencyExchange} w="24px" h="24px" color="brand.500" />
                </Flex>
                <Box>
                    <Text fontSize="lg" fontWeight="700" color={titleColor}>Taux de change</Text>
                    <Text fontSize="sm" color={subColor}>
                        Une ligne par direction : EUR → MGA et MGA → EUR sont deux taux distincts.
                        La marge est ajoutée au montant du payeur, jamais retirée à l'artiste. Une
                        direction absente ou inactive refuse le paiement, sans repli 1:1.
                    </Text>
                </Box>
            </Flex>
            <Flex align="center" gap="10px">
                <Button size="sm" leftIcon={<Icon as={MdRefresh} />} onClick={onRefresh} isLoading={loading}>
                    Actualiser
                </Button>
                <Button size="sm" colorScheme="brand" leftIcon={<Icon as={MdAdd} />} onClick={onCreate}>
                    Ajouter une direction
                </Button>
            </Flex>
        </Flex>
    );
}

/**
 * Exchange rate management page: quick seeding of the opening pairs, then the
 * table of every configured direction with per-row edition.
 * @returns {React.ReactElement}
 */
function CurrencyRates() {
    const { rates, loading, error, refresh } = useCurrencyRates();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [editing, setEditing] = useState(null);

    const openCreate = useCallback(() => {
        setEditing(null);
        onOpen();
    }, [onOpen]);

    const openEdit = useCallback((rate) => {
        setEditing(rate);
        onOpen();
    }, [onOpen]);

    return (
        <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
            <PageHeader loading={loading} onRefresh={refresh} onCreate={openCreate} />
            <QuickSeedPanel onSeeded={refresh} />
            <RatesTable rates={rates} loading={loading} error={error} onEdit={openEdit} />
            <RateFormDialog isOpen={isOpen} onClose={onClose} rate={editing} onSaved={refresh} />
        </Box>
    );
}

export default CurrencyRates;
